/* eslint-disable no-shadow */

export enum HealthMonitorCodes {
    NO_ERROR = 0,
    DEVICE_CODE_INTERNAL_ERROR_1 = 10,
    DEVICE_CODE_INTERNAL_ERROR_2 = 11,
    DEVICE_CODE_INTERNAL_ERROR_3 = 12,
    DEVICE_CODE_INTERNAL_ERROR_4 = 13,
    DEVICE_CODE_CLOCK_SKEW_ERROR = 17,
    DEVICE_CODE_MINIMUM_BROWSER_VERSION_ERROR = 18,
    CAMERA_CODE_NO_CAMERA_ERROR = 1001,
    CAMERA_CODE_CAMERA_OPEN_ERROR = 1002,
    CAMERA_CODE_CAMERA_MISSING_PERMISSIONS_ERROR = 1005,
    LICENSE_CODE_INTERNAL_ERROR_18 = 2001,
    LICENSE_CODE_ACTIVATION_LIMIT_REACHED_ERROR = 2002,
    LICENSE_CODE_METER_ATTRIBUTE_USES_LIMIT_REACHED_ERROR = 2003,
    LICENSE_CODE_AUTHENTICATION_FAILED_ERROR = 2004,
    LICENSE_CODE_INTERNAL_ERROR_1 = 2006,
    LICENSE_CODE_INVALID_LICENSE_KEY_ERROR = 2007,
    LICENSE_CODE_INTERNAL_ERROR_2 = 2008,
    LICENSE_CODE_INTERNAL_ERROR_3 = 2009,
    LICENSE_CODE_REVOKED_LICENSE_ERROR = 2010,
    LICENSE_CODE_INTERNAL_ERROR_4 = 2011,
    LICENSE_CODE_INTERNAL_ERROR_5 = 2012,
    LICENSE_CODE_INTERNAL_ERROR_7 = 2014,
    LICENSE_CODE_INTERNAL_ERROR_8 = 2015,
    LICENSE_CODE_INTERNAL_ERROR_9 = 2016,
    LICENSE_CODE_LICENSE_EXPIRED_ERROR = 2017,
    LICENSE_CODE_LICENSE_SUSPENDED_ERROR = 2018,
    LICENSE_CODE_TOKEN_EXPIRED_ERROR = 2020,
    LICENSE_CODE_DEVICE_DEACTIVATED_ERROR = 2021,
    LICENSE_CODE_INTERNAL_ERROR_10 = 2022,
    LICENSE_CODE_INTERNAL_ERROR_11 = 2023,
    LICENSE_CODE_NETWORK_ISSUES_ERROR = 2024,
    LICENSE_CODE_SSL_HANDSHAKE_ERROR = 2025,
    LICENSE_CODE_INTERNAL_ERROR_16 = 2030,
    LICENSE_CODE_INPUT_LICENSE_KEY_EMPTY_ERROR = 2032,
    LICENSE_CODE_INPUT_FINGERPRINT_EMPTY_ERROR = 2033,
    LICENSE_CODE_INPUT_PRODUCT_ID_ILLEGAL_ERROR = 2034,
    LICENSE_CODE_CANNOT_OPEN_FILE_FOR_READ_ERROR = 2035,
    LICENSE_CODE_MONTHLY_USAGE_TRACKING_REQUIRES_SYNC_ERROR = 2036,
    LICENSE_CODE_SSL_HANDSHAKE_DEVICE_DATE_ERROR = 2037,
    LICENSE_CODE_SSL_HANDSHAKE_CERTIFICATE_EXPIRED_ERROR = 2038,
    LICENSE_CODE_MIN_SDK_ERROR = 2039,
    LICENSE_CODE_MISSING_SDK_VERSION = 2040,
    LICENSE_CODE_FORBIDDEN_LICENSE_TYPE_ERROR = 2041,
    LICENSE_CODE_NETWORK_TIMEOUT_ERROR = 2042,
    MEASUREMENT_CODE_MISDETECTION_DURATION_EXCEEDS_LIMIT_ERROR = 3003,
    MEASUREMENT_CODE_INVALID_RECENT_DETECTION_RATE_ERROR = 3004,
    MEASUREMENT_CODE_LICENSE_ACTIVATION_FAILED_ERROR = 3006,
    MEASUREMENT_CODE_INVALID_MEASUREMENT_AVERAGE_DETECTION_RATE_ERROR = 3008,
    MEASUREMENT_CODE_TOO_MANY_FRAMES_INORDER_ERROR = 3009,
    MEASUREMENT_CODE_MISDETECTION_DURATION_EXCEEDS_LIMIT_WARNING = 3500,
    MEASUREMENT_CODE_INVALID_RECENT_FPS_RATE_WARNING = 3505,
    MEASUREMENT_CODE_MEASUREMENT_MISPLACED_FRAME_WARNING = 3506,
    VITAL_SIGN_CODE_BLOOD_PRESSURE_PROCESSING_FAILED_WARNING = 4505,
    VITAL_SIGN_CODE_MEASURING_WITH_NO_ENABLED_VITAL_SIGNS_WARNING = 4506,
    INITIALIZATION_CODE_INTERNAL_ERROR_1 = 7001,
    INITIALIZATION_CODE_INVALID_PROCESSING_TIME_ERROR = 7002,
    INITIALIZATION_CODE_SESSION_LISTENERS_MISMATCH = 7003,
    INITIALIZATION_CODE_INVALID_LICENSE_FORMAT = 7005,
    INITIALIZATION_CODE_UNSUPPORTED_USER_WEIGHT = 7007,
    INITIALIZATION_CODE_UNSUPPORTED_USER_AGE = 7008
}

export interface OrientationHelper {
    resetValidOrientation(): void;
    isOrientationValid(): boolean;
}

export enum DeviceOrientation {
    PORTRAIT = 0,
    LANDSCAPE_LEFT = 1,
    LANDSCAPE_RIGHT = 2
}

export enum ImageRotation {
    ROTATION_0 = 0,
    ROTATION_90 = 90,
    ROTATION_270 = 270,
    ROTATION_NEGATIVE_90 = -90,
    ROTATION_LANDSCAPE_FALLBACK = 1
}

export declare const MAP_CONSTANT_ORIENTATION_TO_IMAGE_ROTATION: {
    0: ImageRotation[];
    2: ImageRotation[];
    1: ImageRotation[];
};

export enum HealthMonitorMessage {
    HEALTH_MONITOR_HEART_RATE = 1,
    HEALTH_MONITOR_BREATHING_RATE = 3,
    HEALTH_MONITOR_SDNN = 4,
    HEALTH_MONITOR_STRESS_LEVEL = 5,
    HEALTH_MONITOR_REPORT = 100
}

export enum ConfidenceLevel {
    UNKNOWN = 0,
    LOW = 1,
    MEDIUM = 2,
    HIGH = 3
}

export type VitalSignsResults = {
    results: VitalSigns;
    type?: VitalSignType[];
};

export type VitalSign<T> = {
    value?: T;
} & any;

export type HeartRateSign = VitalSign<number> & {
    confidenceLevel?: ConfidenceLevel;
};

export type OxygenSaturationSign = VitalSign<number>;

export type BreathingRateSign = VitalSign<number> & {
    confidenceLevel?: ConfidenceLevel;
};

export type SDNNSign = VitalSign<number> & {
    confidenceLevel?: ConfidenceLevel;
};

export type StressLevelSign = VitalSign<number>;

export type StressIndexSign = VitalSign<number>;

export type BloodPressureSign = VitalSign<BloodPressureValue>;

export type RRISign = VitalSign<RRIValue[]> & {
    confidenceLevel?: ConfidenceLevel;
};

export type MeanRRISign = VitalSign<number> & {
    confidenceLevel?: ConfidenceLevel;
};

export type RmssdSign = VitalSign<number>;

export type Sd1Sign = VitalSign<number>;

export type Sd2Sign = VitalSign<number>;

export type PRQSign = VitalSign<number> & {
    confidenceLevel?: ConfidenceLevel;
};

export type PnsIndexSign = VitalSign<number>;

export type PnsZoneSign = VitalSign<number>;

export type SnsIndexSign = VitalSign<number>;

export type SnsZoneSign = VitalSign<number>;

export type WellnessLevelSign = VitalSign<number>;

export type WellnessIndexSign = VitalSign<number>;

export type LfhfSign = VitalSign<number>;

export type HemoglobinSign = VitalSign<number>;

export type HemoglobinA1cSign = VitalSign<number>;

export type VitalSigns = {
    heartRate?: HeartRateSign;
    oxygenSaturation?: OxygenSaturationSign;
    breathingRate?: BreathingRateSign;
    sdnn?: SDNNSign;
    stressLevel?: StressLevelSign;
    stressIndex?: StressIndexSign;
    bloodPressure?: BloodPressureSign;
    rri?: RRISign;
    meanRri?: MeanRRISign;
    rmssd?: RmssdSign;
    sd1?: Sd1Sign;
    sd2?: Sd2Sign;
    prq?: PRQSign;
    pnsIndex?: PnsIndexSign;
    pnsZone?: PnsZoneSign;
    snsIndex?: SnsIndexSign;
    snsZone?: SnsZoneSign;
    wellnessLevel?: WellnessLevelSign;
    wellnessIndex?: WellnessIndexSign;
    lfhf?: LfhfSign;
    hemoglobin?: HemoglobinSign;
    hemoglobinA1c?: HemoglobinA1cSign;
    isCompleted?: boolean;
    info?: InfoData;
};

export type RRIValue = {
    timestamp: number;
    interval: number;
};

export type BloodPressureValue = {
    systolic: number;
    diastolic: number;
};

export enum StressLevel {
    UNKNOWN = 0,
    LOW = 1,
    NORMAL = 2,
    MILD = 3,
    HIGH = 4,
    EXTREME = 5
}

export enum WellnessLevel {
    UNKNOWN = 0,
    LOW = 1,
    NORMAL = 2,
    HIGH = 3
}

export enum SnsZone {
    UNKNOWN = 0,
    LOW = 1,
    NORMAL = 2,
    HIGH = 3
}

export enum PnsZone {
    UNKNOWN = 0,
    LOW = 1,
    NORMAL = 2,
    HIGH = 3
}

export enum VitalSignType {
    HEART_RATE = "heartRate",
    OXYGEN_SATURATION = "oxygenSaturation",
    BREATHING_RATE = "breathingRate",
    SDNN = "sdnn",
    STRESS_LEVEL = "stressLevel",
    STRESS_INDEX = "stressIndex",
    RRI = "rri",
    MEAN_RRI = "meanRri",
    BLOOD_PRESSURE = "bloodPressure",
    RMSSD = "rmssd",
    SD1 = "sd1",
    SD2 = "sd2",
    PRQ = "prq",
    PNS_INDEX = "pnsIndex",
    PNS_ZONE = "pnsZone",
    SNS_INDEX = "snsIndex",
    SNS_ZONE = "snsZone",
    WELLNESS_INDEX = "wellnessIndex",
    WELLNESS_LEVEL = "wellnessLevel",
    LFHF = "lfhf",
    HEMOGLOBIN = "hemoglobin",
    HEMOGLOBIN_A1C = "hemoglobinA1c"
}

export interface Size {
    height: number;
    width: number;
}

export interface Rect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface Scale {
    width: number;
    height: number;
}

export interface FrameData {
    buffer: ArrayBufferLike;
    size: Size;
    timestamp: number;
    format: any;
}

export enum AlertDomains {
    ALERT_DOMAIN_DEVICE = 0,
    ALERT_DOMAIN_CAMERA = 1000,
    ALERT_DOMAIN_LICENSE = 2000,
    ALERT_DOMAIN_MEASUREMENT = 3000,
    ALERT_DOMAIN_VITAL_SIGNS = 4000,
    ALERT_DOMAIN_RECORDER = 5000,
    ALERT_DOMAIN_SESSION = 6000,
    ALERT_DOMAIN_INITIALIZATION = 7000
}

export interface AlertData {
    code: number;
    domain?: AlertDomains;
}

export interface LicenseInfo {
    onEnabledVitalSigns?: (EnabledVitalSigns: any) => void;
    onOfflineMeasurement?: (OfflineMeasurements: any) => void;
    onActivation?: (activationId: string) => void;
}

export interface LicenseOptions {
    readonly licenseInfo?: LicenseInfo;
    readonly onLicenseError?: (errorData: AlertData) => void;
}

export interface EnabledVitalSigns {
    isEnabledHeartRate: boolean;
    isEnabledBreathingRate: boolean;
    isEnabledSpo2: boolean;
    isEnabledSdnn: boolean;
    isEnabledStressLevel: boolean;
    isEnabledStressIndex: boolean;
    isEnabledBloodPressure: boolean;
    isEnabledRri: boolean;
    isEnabledMeanRri: boolean;
    isEnabledRmssd: boolean;
    isEnabledSd1: boolean;
    isEnabledSd2: boolean;
    isEnabledPrq: boolean;
    isEnabledSnsIndex: boolean;
    isEnabledSnsZone: boolean;
    isEnabledPnsIndex: boolean;
    isEnabledPnsZone: boolean;
    isEnabledWellnessLevel: boolean;
    isEnabledWellnessIndex: boolean;
    isEnabledLfhf: boolean;
    isEnabledHemoglobin: boolean;
    isEnabledHemoglobinA1c: boolean;
}

export interface OfflineMeasurements {
    offlineMeasurements: number;
    offlineMeasurementsRemaining: number;
    measurementSecondsEnd: number;
}

export interface OnStatusUpdate {
    (isMeasuringAllowed: boolean): void;
}

export enum CryptlexErrors {
    NO_ERROR = 0,
    ACTIVATION_LIMIT_REACHED = 2002,
    METER_ATTRIBUTE_USES_LIMIT_REACHED = 2003,
    AUTHENTICATION_FAILED = 2004,
    INVALID_PRODUCT_ID = 2006,
    INVALID_LICENSE_KEY = 2007,
    INVALID_LICENSE_TYPE = 2008,
    VM_ACTIVATION_NOT_ALLOWED = 2009,
    REVOKED_LICENSE = 2010,
    COUNTRY_NOT_ALLOWED = 2011,
    IP_ADDRESS_NOT_ALLOWED = 2012,
    TRIAL_NOT_ALLOWED = 2014,
    TRIAL_ACTIVATION_LIMIT_REACHED = 2015,
    SERVER_RESPONSE_FAILED_AUTHENTICATION = 2016,
    LICENSE_EXPIRED = 2017,
    LICENSE_SUSPENDED = 2018,
    TOKEN_EXPIRED = 2020,
    DEVICE_WAS_DEACTIVATED = 2021,
    UNKNOWN_ERROR_OCCURRED = 2023
}

export enum HttpMethod {
    GET = 0,
    POST = 1,
    PATCH = 2,
    PUT = 3,
    DELETE = 4
}

export enum SessionState {
    INIT = 0,
    ACTIVE = 1,
    MEASURING = 2,
    STOPPING = 3,
    TERMINATED = 4
}

export enum ImageValidity {
    VALID = 0,
    INVALID_DEVICE_ORIENTATION = 1,
    INVALID_ROI = 2,
    TILTED_HEAD = 3,
    FACE_TOO_FAR = 4,
    UNEVEN_LIGHT = 5
}

export interface OnFaceDetected {
    (isFaceRect: boolean): void;
}

export interface OnImageData {
    (imageValidity: ImageValidity): void;
}

export interface OnVitalSign {
    (VitalSign: VitalSigns): void;
}

export interface OnFinalResults {
    (vitalSignsResults: VitalSignsResults): void;
}

export interface OnError {
    (errorData: AlertData): void;
}

export interface OnWarning {
    (warningData: AlertData): void;
}

export type ErrorListener = () => void;

export interface OnStateChange {
    (state: SessionState): void;
}

export enum Gender {
    UNSPECIFIED = 0,
    MALE = 1,
    FEMALE = 2
}

export interface SubjectDemographic {
    gender: Gender;
    age: number;
    weight: number;
}

export declare const SUBJECT_DEMOGRAHIC_THRESHOLD: {
    age: {
        min: number;
        max: number;
    };
    weight: {
        min: number;
        max: number;
    };
};

export interface FaceSessionOptions {
    input: HTMLVideoElement;
    cameraDeviceId: string;
    processingTime: number;
    onError?: OnError;
    onWarning?: OnWarning;
    onFaceDetected?: OnFaceDetected;
    onVitalSign?: OnVitalSign;
    onFinalResults?: OnFinalResults;
    onStateChange?: OnStateChange;
    orientation?: DeviceOrientation;
    subjectDemographic?: SubjectDemographic;
    strictMeasurementGuidance?: boolean;
    onImageData?: OnImageData;
}

export interface HealthMonitorSession {
    start(): void;
    stop(): void;
    terminate(): void;
    getState(): SessionState;
    addOnStateChangeListener(onStateChange: OnStateChange): any;
}

export enum HealthMonitorSessionMode {
    FACE_FRONT_CAMERA = 0
}

export enum InfoType {
    NONE,
    INSTRUCTION,
    SUCCESS
}

export interface InfoData {
    type: InfoType;
    message?: string;
}
